/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   licenses.ts
 *     Description     :   Spec 285 — the per-tenant license (entitlement) vocabulary, shared
 *                         by both planes. `backup` gates Backup Management + the dashboard
 *                         backup tiles; `ops` gates Pipeline Monitoring. Owned by the
 *                         management plane (FCM) in the end state; delivered here for now by
 *                         an interim FCP-local, SuperAdmin-set flag consulted at one point.
 *                         Default is fail-closed (NOT licensed).
 *     Author          :   Peter Dobler
 * =========================================================================================
 */

/** A paid capability a tenant may hold. Extensible as paid modules appear. */
export type License = 'backup' | 'ops';

/** A tenant's resolved license state. */
export interface TenantLicenses {
  backup: boolean;
  ops: boolean;
}

/** Fail-closed default: nothing licensed (an unknown tenant, or no row). */
export const NO_LICENSES: TenantLicenses = Object.freeze({ backup: false, ops: false });

/** Fully licensed — used for a SuperAdmin (license-exempt) and the grandfathered live tenant. */
export const ALL_LICENSES: TenantLicenses = Object.freeze({ backup: true, ops: true });

// ── Spec 286 — the FCM-written licence, resolved (contracts/licence-reader.md) ─────────────

/**
 * - `exempt`       SuperAdmin: no read, everything allowed (ADR 0148).
 * - `active`       an `active` row (add-ons per their terms), or — while the cut-over switch is off —
 *                  no row at all (today's behaviour: Governance by role, add-ons off; FR-010).
 * - `plugged`      the plug is pulled: suspended, revoked, an unknown value, or (switch on) no row —
 *                  no functionality at all, Governance included (FR-002/FR-007).
 * - `unverifiable` the read itself failed — never reported as a suspension (FR-011).
 */
export type LicenseState = 'exempt' | 'active' | 'plugged' | 'unverifiable';
export type PlugReason = 'status-suspended' | 'status-revoked' | 'unknown-value' | 'no-row';

export interface ResolvedLicense extends TenantLicenses {
  state: LicenseState;
  /** Set only when `state === 'plugged'`. */
  plugReason: PlugReason | null;
  /** The free base: true iff `state` is `exempt` or `active`. */
  governance: boolean;
  status: 'active' | 'suspended' | 'revoked' | null;
  tier: 'team' | 'growth' | 'business' | 'enterprise' | null;
  limits: { workspaces: number | null; items: number | null; capacities: number | null } | null;
  /** ISO UTC; null = no end date. */
  terms: { opsUntil: string | null; backupUntil: string | null } | null;
  growthFrozenFrom: string | null;
  source: 'manual' | 'fcm' | null;
}

/** Spec 286 — the kinds the growth freeze counts (ADR 0156). */
export type FreezeKind = 'workspaces' | 'items' | 'capacities';

/**
 * Spec 286 — one kind's growth-freeze withholds, summed over the kind's most recent merge run of the
 * CURRENT freeze (contracts/portal-gating.md). `tables` names the target tables the run withheld from.
 */
export interface WithheldKind {
  kind: FreezeKind;
  withheldNew: number;
  withheldChildren: number;
  limit: number;
  count: number;
  /** ISO UTC — the freeze date the run held. */
  growthFrozenFrom: string;
  /** ISO UTC — the latest row of the run. */
  recordedAt: string;
  mergeRunId: string;
  tables: string[];
}

/** The licence `/api/auth/session` returns: the resolved licence, plus the freeze's withholds for
 *  Admins and SuperAdmin (`null` for every other role). */
export interface SessionLicense extends ResolvedLicense {
  withheld: WithheldKind[] | null;
}

/** Everything off and nothing known — the base of the outcomes below. */
const NO_LICENCE_DETAIL: Omit<ResolvedLicense, 'state' | 'plugReason'> = Object.freeze({
  governance: false, backup: false, ops: false,
  status: null, tier: null, limits: null, terms: null, growthFrozenFrom: null, source: null,
});

/** SuperAdmin: everything allowed, and no licence is read (ADR 0148). */
export const EXEMPT_LICENSE: ResolvedLicense = Object.freeze({
  ...NO_LICENCE_DETAIL, state: 'exempt', plugReason: null, governance: true, backup: true, ops: true,
});

/** The licence read failed: never a suspension and never a licence (FR-011). */
export const UNVERIFIABLE_LICENSE: ResolvedLicense = Object.freeze({
  ...NO_LICENCE_DETAIL, state: 'unverifiable', plugReason: null,
});
